import { Buffer } from 'buffer'
import moment from 'moment-timezone'

import {
  triggerConfirmationPageLoadEvent,
  triggerPurchaseEvent,
} from 'services/Analytics'
import { emailVerification } from 'services/User'
import {
  CONFIRMATION_GA4_EVENTS,
  DISCOUNT,
  KLAVIYO_EVENTS,
} from 'utils/constants/Enums'
import {
  GENERAL_APPOINTMENT_ERROR_MESSAGE,
  SIGNUP_MESSAGES,
} from 'utils/constants/Messages'
import { AppData, Location } from 'utils/types/appTypes'
import { OptionsTypes } from 'utils/types/enhancementTypes'
import { PurchaseData } from 'utils/types/ga4eventsTypes'
import { AnalyticsLocationData } from 'utils/types/locationTypes'
import { BookedAppointmentResponse } from 'utils/types/userTypes'
import {
  CUSTOMER_SESSION_TOKEN,
  LOCATION_STATE_CONFIG,
  NO_TOKEN_FOUND,
} from '../constants/Helpers'
import { REGEX } from '../constants/regex'
import { AppointmentConfirmationData } from '../types/appointmentTypes'
import { emailSchema } from '../yupSchema'

/**
 * @param apiKey - string
 *
 * Returns a token to access public client API
 * in a string.
 */

type publicClientToken = (apiKey: string) => string

export const generatePublicClientToken: publicClientToken = (apiKey) => {
  const payload = `${apiKey}:`
  const http_basic_credentials = Buffer.from(payload, 'utf8').toString('base64')

  return http_basic_credentials
}

/**
 * @param firstName - string
 * @param lastName - string
 *
 * Returns a avatar name first & surnames first letter
 * in a string.
 */

type generateAvatarTextType = (firstName: string, lastName: string) => string

export const generateAvatarText: generateAvatarTextType = (
  firstName,
  lastName
) => {
  return `${firstName.charAt(0).toLocaleUpperCase()}${lastName
    .charAt(0)
    .toLocaleUpperCase()}`
}

/**
 * @param keyName - string
 * @param value - string
 *
 * set localstorage item
 */
export const setLocalstorageItem = (keyName: string, value: string) => {
  window.localStorage.setItem(keyName, value)
}

/**
 * @param keyName - string
 *
 * Returns a localstorage value based on key name
 * in a string.
 */
export const getLocalstorageItem = (keyName: string) => {
  return window.localStorage.getItem(keyName)
}

export const removeLocalstorageItem = (keyName: string) => {
  return window.localStorage.removeItem(keyName)
}

export const emailVerifier = (emailId: string): boolean => {
  try {
    if (emailId) {
      emailSchema.validateSync(emailId)
      return true
    }
    return false
  } catch (error) {
    return false
  }
}
export const containSpecialChar = (name: string): boolean => {
  return REGEX.NAME.test(name)
}

export const textTrimmer = (data: string): string => {
  return data.trim()
}

export const numberOnly = (data: string): boolean => {
  return REGEX.PHONE_NUMBER.test(data)
}

export const cardTypeValidation = (data: string): string => {
  let cardNumber = data.replace(/[_ ]/g, '')
  let cardType = ''
  if (cardNumber) {
    if (REGEX.AMERICAN_EXP_CARD_REGEX.test(cardNumber)) {
      cardType = 'AMEX'
    } else {
      cardType = 'ERROR'
    }
  }
  return cardType
}

/**
 * @param condition - boolean that will be checked for response
 * @param truthyClass - class applied when the condition is true
 * @param falsyClass - class applied when the condition is false
 * @param commonClass - classes which are common in both condition
 *
 * Returns a string with the list of classes separated by spaces
 * in a string.
 */

type ConditionalClasses = (
  condition: boolean,
  truthyClass: string,
  falsyClass?: string,
  commonClass?: string
) => string

export const conditionalClasses: ConditionalClasses = (
  condition,
  truthyClass,
  falsyClass = '',
  commonClass = ''
) => {
  return `${commonClass} ${condition ? truthyClass : falsyClass}`
}

/**
 * @param cost - number
 * @param tailingZero - boolean
 * @param divider - number
 * Returns a formatted cost with $ value
 * in a string.
 */

type PriceFormatter = (
  cost?: number,
  tailingZero?: boolean,
  divider?: number
) => string

export const formattedCost: PriceFormatter = (
  cost,
  tailingZero = true,
  divider = 100
) => {
  if (!cost) return '$0'
  const num = cost / divider // Divide the cost based on the requirements
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: tailingZero || num % 1 !== 0 ? 2 : 0,
  })

  return formatter.format(num).replace(/\D00$/, '')
}

export const tokenValidator = () => {
  const tokenValid = getLocalstorageItem(CUSTOMER_SESSION_TOKEN)
  if (!tokenValid) {
    throw new Error(NO_TOKEN_FOUND)
  }
}

export const findDateDifference = (futureDate: Date, currentDate: Date) => {
  const timeDifference = futureDate.getTime() - currentDate.getTime()
  const dateDiff = Math.floor(timeDifference / (1000 * 60 * 60 * 24))
  return dateDiff
}

export const chooseToken = (
  appointmentConfMasterData: AppointmentConfirmationData,
  generateToken: string
) => {
  if (appointmentConfMasterData.isExistingCard) {
    return appointmentConfMasterData.selectedCardToken
      ? appointmentConfMasterData.selectedCardToken
      : generateToken
  } else {
    return generateToken
  }
}

export const getOpeningDate = (loc: Location) => {
  if (loc.openDate && new Date(loc.openDate) > new Date()) {
    return new Date(`${loc.openDate} 00:00`)
  }
}

export const getSelectedDate = (selectedDate: Date, loc: Location) => {
  const openingDate = getOpeningDate(loc)
  if (openingDate) {
    return openingDate > selectedDate ? openingDate : selectedDate
  }
  return selectedDate
}

export const getBannerText = (loc: Location) => {
  const openingDate = getOpeningDate(loc)

  // get 1 week before opening date to show banner and animation
  const beforeOpeningDate = moment(openingDate).clone().subtract(7, 'days')

  if (openingDate && beforeOpeningDate.toDate() > new Date()) {
    return `Hey there! Our doors open on ${
      openingDate.getMonth() + 1
    }/${openingDate.getDate()}, so make sure to book after that date.`
  }
}

export const getFacialType = (appData: AppData) => {
  if (appData.availableFacials.length === 1) {
    return appData.availableFacials?.[0]?.name
  }
  return appData.selectedFacial
}

export const getLocationDataForAnalytics = (
  location: Location
): AnalyticsLocationData => {
  return {
    locationId: location.locationId,
    name: location.name,
    address: location.address,
  }
}

export const getErrorMessage = (message: string) => {
  if (!message) return GENERAL_APPOINTMENT_ERROR_MESSAGE
  return `${message} Please contact hello@heydayskincare.com if further assistance is needed.`
}

export const cleanPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return ''
  // Remove non-numeric characters
  const cleanedNumber = phoneNumber.replace(/\D/g, '')
  // Check if the number already starts with '1'
  if (cleanedNumber.startsWith('1')) {
    return '+' + cleanedNumber
  }
  return '+1' + cleanedNumber
}

export const isEnableSummerPromo = (
  appointmentDate: Date | string,
  location: string
): boolean => {
  const excludedLocations = [
    'Lowry',
    'University Hills',
    'Tennyson',
    'Kirkland',
    'Plymouth Meeting',
    'Cobble Hill',
  ]

  if (!appointmentDate) return false
  // Define the start and end dates of the range
  const startDate = moment('2024-06-11') // 2024-06-13 is actual date, 2 days buffer for testing
  const endDate = moment('2024-07-07')
  // Check if the appointment date is within the range
  return (
    moment(appointmentDate).isBetween(startDate, endDate, null, '[]') &&
    !excludedLocations.includes(location)
  )
}

export function getLatestAppointment(
  appointments: BookedAppointmentResponse[]
): BookedAppointmentResponse | null {
  if (!appointments?.length) return null
  return (
    appointments.find((appointment) => appointment.state !== 'CANCELLED') ??
    null
  )
}

export function applyDiscount(amount: number, discount: DISCOUNT): number {
  if (discount < 0 || discount > 100) {
    throw new Error('Discount percentage not valid')
  }
  return amount - amount * (discount / 100)
}

export const triggerGA4Events = (eventType: string, appMasterData: AppData) => {
  let ga4Data: PurchaseData = {
    type: eventType,
    currency: 'USD',
    value: appMasterData?.cart?.summary?.subtotal / 100,
    item_name: 'Monthly Membership',
    location_name: appMasterData.location.name ?? '',
    location_state:
      LOCATION_STATE_CONFIG[appMasterData?.location?.address?.state] ?? '',
    timeslot_selected:
      moment
        .tz(appMasterData?.selectedDate, appMasterData?.location?.tz)
        .format('H:mm') ?? '',
    date_selected:
      moment(appMasterData.selectedDate).format('DD-MM-YYYY') ?? '',
    userId: appMasterData?.userInfo?.boulevard_id ?? '',
  }

  if (eventType === 'Booking') {
    ga4Data = {
      ...ga4Data,
      type: 'Booking',
      coupon: appMasterData.cart?.promoCodes?.[0]?.code ?? '',
      items:
        appMasterData?.selectedEnhancements?.map((item) => item.name) ?? [],
      item_name: appMasterData.selectedFacial,
      transaction_id: appMasterData?.cart?.appointmentId ?? '',
      value:
        appMasterData?.cart?.summary?.discountAmount > 0
          ? appMasterData?.cart?.summary?.total / 100
          : appMasterData?.cart?.summary?.subtotal / 100,
    }
  }

  triggerPurchaseEvent?.(ga4Data)
  if (eventType !== 'Booking') {
    triggerConfirmationPageLoadEvent(
      CONFIRMATION_GA4_EVENTS.MEMBERSHIP_PURCHASE_GA4,
      appMasterData,
      'Membership'
    )
  }
}

export const generateAbandonUrl = (
  condition: string,
  appMasterData: AppData,
  extra?: any
) => {
  const baseURL = process.env.REACT_APP_ABANDON_BASE_URL
  const isDevEnv = process.env.REACT_APP_ENV === 'development'
  const updatedURL = `?location=${appMasterData.location.locationId.replace(/^.*:/, '')}&service=${appMasterData.category.categoryId.replace(/^.*:/, '')}`
  const enhancementIds = (
    appMasterData.selectedEnhancements as OptionsTypes[]
  ).map((e) =>
    isDevEnv
      ? e.boulevard_sandbox_id?.replace(/^.*:/, '')
      : e.boulevard_id?.replace(/^.*:/, '')
  )
  const bookNow = `${process.env.REACT_APP_ENV === 'production' ? '#book-now' : ''}`

  const staffId = appMasterData.filteredEstheticians?.[0]?.staff?.id?.replace(
    /^.*:/,
    ''
  )
  const hasEnhancements = enhancementIds.length > 0
  const hasStaff = !!staffId

  const buildBookingUrl = (params = {}, useUpdated = true) => {
    const query = new URLSearchParams({
      ...params,
      abandon: 'true',
    }).toString()
    const base = useUpdated ? `${baseURL}${updatedURL}` : baseURL
    return `${base}&${query}${bookNow}`
  }

  const handlers = {
    [KLAVIYO_EVENTS.LOCATION_SELECTED]: () =>
      `${baseURL}?city=${appMasterData.location.name}&abandon=true${bookNow}`,

    [KLAVIYO_EVENTS.BASE_SERVICE_SELECTED]: () => buildBookingUrl(),

    [KLAVIYO_EVENTS.ENHANCEMENT_SELECTED]: () =>
      buildBookingUrl({ enhancement: enhancementIds }),

    [KLAVIYO_EVENTS.DATETIME_ST_SELECTED]: () => {
      const selectedTime = new Date(extra).toISOString()

      const params = {
        date: selectedTime,
        ...(hasEnhancements && { enhancement: enhancementIds }),
        ...(hasStaff && { staff: staffId }),
      }

      return buildBookingUrl(params)
    },

    [KLAVIYO_EVENTS.CONTRAINDICATIONS_SCREEN_SELECTED]: () => {
      const selectedTime = new Date(extra).toISOString()

      const params = {
        disclaimer: true,
        date: selectedTime,
        ...(hasEnhancements && { enhancement: enhancementIds }),
        ...(hasStaff && { staff: staffId }),
      }

      return buildBookingUrl(params)
    },
  }

  return (
    handlers[condition as keyof typeof handlers]?.() ?? `${baseURL}/${bookNow}`
  )
}

export const verifyEmail = async (
  email: string,
  enqueueSnackbar: any,
  appMasterData: AppData,
  updateData: any
) => {
  try {
    const res = await emailVerification(email)
    const status = res?.customer?.shopify_status
    if (status === 'enabled') {
      updateData({
        userInfo: {
          ...appMasterData.userInfo,
          first_name: res?.customer.first_name ?? '',
          last_name: res?.customer.last_name ?? '',
        },
      })
      return true
    } else if (status === 'invited') {
      enqueueSnackbar(SIGNUP_MESSAGES.SHOPIFY_STATUS_LOGIN, {
        variant: 'warning',
      })
      return false
    } else if (status === 'disabled') {
      enqueueSnackbar(SIGNUP_MESSAGES.SHOPIFY_STATUS_DISABLED, {
        variant: 'warning',
      })
      return false
    } else {
      return true
    }
  } catch (error) {
    console.error('Email verification failed:', error)
    enqueueSnackbar(SIGNUP_MESSAGES.INVALID_EMAIL, { variant: 'error' })
  }
}
